'use client';

import { Stream } from '@cloudflare/stream-react';
import { useEffect, useRef, useState } from 'react';

import { convertToEmbedUrl } from '@/components/Video/VideoUtils.ts';
import { cn } from '@/lib/utils.ts';

import { loadTwitterWidgets } from './loadTwitterWidgets';
import { parseInstagramPostUrl } from './cmsSafeMedia';

const toneStyles = {
    info: 'bg-muted border-border',
    warning: 'bg-yellow-100 border-yellow-300 dark:bg-yellow-950/40 dark:border-yellow-700',
} as const;

export function CmsYouTubeEmbed({ url }: { url: string }) {
    const embedSrc = convertToEmbedUrl(url);

    return (
        <div
            data-no-pdf
            className="mx-auto my-4 aspect-video w-full overflow-hidden rounded-3xl border border-border"
        >
            <iframe
                title="YouTube video"
                src={embedSrc}
                className="h-full w-full"
                referrerPolicy="strict-origin-when-cross-origin"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
            />
        </div>
    );
}

export type CmsCloudflareStreamOrientation = 'landscape' | 'portrait';

/**
 * Cloudflare Stream fills its parent; aspect ratio comes from the outer wrapper.
 *
 * Important: {@link Stream} defaults `responsive` to true, which applies inline `position: relative` and
 * `padding-top` from **video metadata** (usually 16:9). That overrides a portrait `aspect-*` wrapper and
 * keeps the player landscape. We set `responsive={false}` and stretch the iframe so only the outer box
 * controls layout (same outcome as Cloudflare’s manual embed HTML).
 */
export function CmsCloudflareStreamEmbed({
    uid,
    orientation = 'landscape',
}: {
    uid: string;
    orientation?: CmsCloudflareStreamOrientation;
}) {
    const portrait = orientation === 'portrait';

    return (
        <div
            data-no-pdf
            data-testid="cms-cloudflare-stream"
            className={cn(
                'relative my-4 w-full overflow-hidden rounded-3xl border border-border',
                portrait ? 'aspect-[9/16] max-w-lg mx-auto' : 'aspect-video mx-auto',
            )}
        >
            <Stream
                src={uid}
                controls
                responsive={false}
                className={cn(
                    'absolute inset-0 h-full w-full light:bg-muted dark:bg-muted',
                    '[&_iframe]:absolute [&_iframe]:inset-0 [&_iframe]:h-full [&_iframe]:w-full [&_iframe]:border-0',
                )}
            />
        </div>
    );
}

export function CmsCallout({ text, tone }: { text: string; tone: 'info' | 'warning' }) {
    return (
        <div
            className={cn(
                'my-4 rounded-md border p-4 type-body-default',
                toneStyles[tone],
            )}
            data-testid="cms-callout"
            data-tone={tone}
        >
            {text}
        </div>
    );
}

/** Instagram post / reel via official `/embed/` iframe. */
export function CmsInstagramEmbed({ url }: { url: string }) {
    const parsed = parseInstagramPostUrl(url);
    if (!parsed) {
        return null;
    }

    return (
        <div
            data-no-pdf
            data-testid="cms-instagram-embed"
            className="mx-auto my-4 w-full max-w-lg overflow-hidden rounded-3xl border border-border"
        >
            <iframe
                title="Instagram post"
                src={parsed.embedUrl}
                className="w-full min-h-[540px] border-0"
                loading="lazy"
                referrerPolicy="strict-origin-when-cross-origin"
                allowFullScreen
            />
        </div>
    );
}

/**
 * X (Twitter) status via widgets.js `createTweet`.
 * Loads platform script once; fails closed (renders nothing) if the widget cannot mount.
 */
export function CmsXPostEmbed({ statusId }: { statusId: string }) {
    const containerRef = useRef<HTMLDivElement>(null);
    const [failed, setFailed] = useState(false);

    useEffect(() => {
        const el = containerRef.current;
        if (!el || failed) {
            return;
        }

        let cancelled = false;
        el.replaceChildren();

        loadTwitterWidgets()
            .then(api => api.widgets.createTweet(statusId, el, {
                dnt: true,
                align: 'center',
            }))
            .then((node) => {
                if (cancelled) {
                    return;
                }
                if (!node) {
                    setFailed(true);
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setFailed(true);
                }
            });

        return () => {
            cancelled = true;
            el.replaceChildren();
        };
    }, [statusId, failed]);

    if (failed) {
        return null;
    }

    return (
        <div
            data-no-pdf
            data-testid="cms-x-post-embed"
            className="mx-auto my-4 flex w-full max-w-lg justify-center"
        >
            <div ref={containerRef} className="w-full min-w-0" />
        </div>
    );
}
